/**
 * Pure draft rules for the schedule pattern editor: defaults, kind/repeat switching, and structural
 * validity. Kept out of the component so the "what does switching to fortnightly keep?" question has a
 * single testable answer and never depends on a rendered view.
 *
 * The editor works on a draft that carries ALL FOUR kind branches at once — calendar, afterService,
 * perVisit and dates each keep whatever the user last typed into them. Switching tabs only moves
 * `kind`, so a user who flips to Per visit, changes their mind, and flips back finds their weekly
 * pattern still there. Only the branch matching `kind` is handed back on save.
 *
 * The draft also carries two TASK-level dates the editor owns — `startsOn` and `lastServiced` — which
 * are not part of any schedule branch. Save hands back a {@link ScheduleEditorResult} rather than a
 * bare model so the caller can write all three in one go.
 *
 * Every seeded draft is a SAVEABLE model, including the untouched default. A task reaching this editor
 * already has a schedule — the working-day default — so Save must always hand back something real;
 * returning `undefined` for an untouched draft would read as "cancelled" to the card and leave the task
 * on a default the user just opened the editor to leave.
 */

import { SCHEDULE_DATES_MAX, ScheduleAfterServiceAnchor, ScheduleAfterServiceRule, ScheduleCalendarRule, ScheduleDatesRule, ScheduleLeeway, ScheduleRule, ScheduleKind, ScheduleNonWorkingDayPolicy, ScheduleRepeat, ScheduleValidationIssue, ScheduleWeekday, validateTaskSchedule } from '@jms/model/scheduling';
import { ScheduleEditorResult } from './schedule.editor.launcher.contract';

/**
 * Every branch present, so kind switching is a field assignment and never a data loss.
 *
 * `startsOn` and `lastServiced` sit here beside the schedule branches even though neither is part of
 * the schedule object: both are TASK-level fields the editor is the only place that edits. Threading
 * them through as separate dialog state would let a user pick a first-due date and then have it
 * silently discarded by a kind switch — holding them in the one draft makes the whole dialog one
 * atomic answer.
 */
export interface ScheduleEditorDraft {
  kind: ScheduleKind;
  perVisit: { nth: number };
  calendar: ScheduleCalendarRule;
  /**
   * `firstDue` is deliberately absent: the chain seeds from the task's `lastCompleted` (the draft's
   * `lastServiced`) and falls back to the line's effective-from. The editor never writes the
   * deprecated field, and ignores it when a legacy model carries one.
   */
  afterService: ScheduleEditorAfterService;
  dates: ScheduleDatesRule & { dates: string[]; nonWorkingDayPolicy: ScheduleNonWorkingDayPolicy };
  /**
   * The task's own start override, YYYY-MM-DD, or undefined for "with the Service Line". Not part of
   * any schedule branch — it is the task's `startsOn` column, edited here because the first occurrence
   * is a schedule question, and handed back separately on save.
   */
  startsOn?: string;
  /**
   * The task's `lastCompleted`, YYYY-MM-DD, or undefined when nothing is recorded. Only the
   * after-service chain reads it; empty means the chain starts at the line's effective-from.
   */
  lastServiced?: string;
}

/** Seed values a task carries outside its schedule object, handed in and back out around the draft. */
export interface ScheduleEditorTaskDates {
  startsOn?: string;
  lastServiced?: string;
}

/** Monday. A weekly pattern with nothing ticked cannot compile, so the draft always starts with one day. */
const DEFAULT_WEEKDAY: ScheduleWeekday = 1;

/** Fortnightly is a fixed 14-day cadence; only the phase (which week) is the user's to pick. */
const DEFAULT_WEEK_SELECTOR: ScheduleCalendarRule['weekSelector'] = 'A';

/** Quarterly means every 3 months until the user says otherwise. */
const DEFAULT_MONTH_INTERVAL = 3;

/** Repeats whose "On" block is the weekday toggle row — the set that needs `daysOfWeek` to be valid. */
const WEEKDAY_REPEATS: readonly ScheduleRepeat[] = ['weekly', 'fortnightly'];

/** Repeats whose "On" block is day-of-month vs nth-weekday. */
const MONTH_DAY_REPEATS: readonly ScheduleRepeat[] = ['monthly', 'quarterly', 'yearly'];

export function repeatUsesWeekdays(repeat: ScheduleRepeat): boolean {

  return WEEKDAY_REPEATS.includes(repeat);
}

export function repeatUsesMonthDays(repeat: ScheduleRepeat): boolean {

  return MONTH_DAY_REPEATS.includes(repeat);
}

/**
 * Seeds a draft from an existing model, or from nothing for a task that has never been scheduled.
 *
 * Branches the incoming model does not carry get their defaults rather than being left undefined: the
 * whole point of holding all three is that a tab switch has somewhere to land.
 */
/**
 * Leeways are always present in the draft (zero means none) so the steppers bind to a value; `toModel`
 * drops zero leeways and the default anchor, so an untouched schedule saves exactly as before.
 */
export interface ScheduleEditorAfterService {
  interval: number;
  unit: ScheduleAfterServiceRule['unit'];
  earlyLeeway: ScheduleLeeway;
  lateLeeway: ScheduleLeeway;
  anchor: ScheduleAfterServiceAnchor;
}

export function createDraft(model: ScheduleRule | undefined, taskDates?: ScheduleEditorTaskDates): ScheduleEditorDraft {

  return {
    // Calendar is the default kind. A task with no schedule of its own is already due every working
    // day, so a user who opened this editor is naming dates — landing them on the tack-on tab first
    // would make the common case a detour.
    kind: model?.kind ?? 'calendar',
    perVisit: { nth: model?.perVisit?.nth ?? 1 },
    calendar: {
      repeat: model?.calendar?.repeat ?? 'weekly',
      dailyPreset: model?.calendar?.dailyPreset ?? 'everyDay',
      dailyInterval: model?.calendar?.dailyInterval ?? 2,
      daysOfWeek: [...(model?.calendar?.daysOfWeek ?? [DEFAULT_WEEKDAY])],
      weeklyInterval: model?.calendar?.weeklyInterval ?? 1,
      weekSelector: model?.calendar?.weekSelector ?? DEFAULT_WEEK_SELECTOR,
      monthlyOn: model?.calendar?.monthlyOn ?? 'dayOfMonth',
      daysOfMonth: [...(model?.calendar?.daysOfMonth ?? [1])],
      nths: [...(model?.calendar?.nths ?? [1])],
      weekday: model?.calendar?.weekday ?? DEFAULT_WEEKDAY,
      monthInterval: model?.calendar?.monthInterval ?? DEFAULT_MONTH_INTERVAL,
      months: [...(model?.calendar?.months ?? [])],
      ends: model?.calendar?.ends ?? { type: 'never' },
      nonWorkingDayPolicy: model?.calendar?.nonWorkingDayPolicy ?? 'keep',
    },
    afterService: {
      interval: model?.afterService?.interval ?? 6,
      unit: model?.afterService?.unit ?? 'months',
      earlyLeeway: { value: model?.afterService?.earlyLeeway?.value ?? 0, unit: model?.afterService?.earlyLeeway?.unit ?? 'months' },
      lateLeeway: { value: model?.afterService?.lateLeeway?.value ?? 0, unit: model?.afterService?.lateLeeway?.unit ?? 'months' },
      anchor: model?.afterService?.anchor ?? 'completion',
    },
    dates: {
      dates: sortUniqueDates(model?.dates?.dates ?? []),
      nonWorkingDayPolicy: model?.dates?.nonWorkingDayPolicy ?? 'keep',
    },
    startsOn: taskDates?.startsOn,
    lastServiced: taskDates?.lastServiced,
  };
}

function toAfterServiceModel(afterService: ScheduleEditorAfterService): ScheduleAfterServiceRule {

  const { interval, unit, earlyLeeway, lateLeeway, anchor } = afterService;

  return {
    interval,
    unit,
    ...(earlyLeeway.value > 0 ? { earlyLeeway: { ...earlyLeeway } } : {}),
    ...(lateLeeway.value > 0 ? { lateLeeway: { ...lateLeeway } } : {}),
    ...(anchor === 'anniversary' ? { anchor } : {}),
  };
}

/** Ascending and deduplicated — the shape the model documents and `validateTaskSchedule` insists on. */
export function sortUniqueDates(dates: readonly string[]): string[] {

  return Array.from(new Set(dates)).sort();
}

/**
 * Adds a date to the list, keeping it sorted, unique and inside the cap.
 *
 * Returns the list unchanged when the date is already listed or the cap is reached, so the caller
 * never has to branch: an at-capacity list simply stops growing rather than silently dropping its
 * oldest entry, which would lose a date the user deliberately picked.
 */
export function addScheduleDate(dates: readonly string[], date: string): string[] {

  if (!date || dates.includes(date) || dates.length >= SCHEDULE_DATES_MAX) return [...dates];

  return sortUniqueDates([...dates, date]);
}

export function removeScheduleDate(dates: readonly string[], date: string): string[] {

  return dates.filter(value => value !== date);
}

/**
 * Narrows the draft to the branch the active kind actually persists.
 *
 * Only the live branch ships. Writing all three would leave a stale "every 2nd visit" on a calendar
 * schedule, and the engine's describe/compile read whichever branch matches `kind` — a stale sibling
 * would be invisible in the UI but real in the database.
 */
export function toModel(draft: ScheduleEditorDraft): ScheduleRule {

  if (draft.kind === 'perVisit') {
    return { kind: 'perVisit', perVisit: { nth: draft.perVisit.nth } };
  }

  // `firstDue` is never written: the chain seeds from the task's lastCompleted and falls back to the
  // line's effective-from, so a third seed on the schedule would be a second answer to one question.
  if (draft.kind === 'afterService') {
    return { kind: 'afterService', afterService: toAfterServiceModel(draft.afterService) };
  }

  if (draft.kind === 'dates') {
    return { kind: 'dates', dates: { dates: sortUniqueDates(draft.dates.dates), nonWorkingDayPolicy: draft.dates.nonWorkingDayPolicy } };
  }

  return { kind: 'calendar', calendar: pruneCalendar(draft.calendar) };
}

/**
 * What Save hands the card: the schedule plus the two task-level dates.
 *
 * `startsOn` only ships on a calendar schedule. Per-visit creates no dates of its own, a dates list
 * already names its own first day, and after-service chains off `lastCompleted` — carrying a start
 * override on any of them would store a value nothing reads and that reappears if the user later
 * switches back to calendar.
 */
export function toResult(draft: ScheduleEditorDraft): ScheduleEditorResult {

  return {
    schedule: toModel(draft),
    startsOn: draft.kind === 'calendar' ? draft.startsOn : undefined,
    lastCompleted: draft.kind === 'afterService' ? draft.lastServiced : undefined,
  };
}

/** Whether this kind's first occurrence is a date the user may override — the calendar repeats only. */
export function kindUsesFirstDue(kind: ScheduleKind): boolean {

  return kind === 'calendar';
}


/**
 * Drops the calendar fields the chosen repeat does not read.
 *
 * Persisting every field would make two visually identical schedules compare unequal, and would leave
 * a `dailyInterval` on a monthly rule for the next reader to wonder about.
 */
function pruneCalendar(calendar: ScheduleCalendarRule): ScheduleCalendarRule {

  const base: ScheduleCalendarRule = {
    repeat: calendar.repeat,
    ends: calendar.ends,
    nonWorkingDayPolicy: calendar.nonWorkingDayPolicy,
  };

  if (calendar.months?.length) base.months = [...calendar.months].sort((a, b) => a - b);

  if (calendar.repeat === 'daily') {
    base.dailyPreset = calendar.dailyPreset ?? 'everyDay';
    if (base.dailyPreset === 'everyNDays') base.dailyInterval = calendar.dailyInterval ?? 2;

    return base;
  }

  if (repeatUsesWeekdays(calendar.repeat)) {
    base.daysOfWeek = [...(calendar.daysOfWeek ?? [])].sort((a, b) => a - b);
    if (calendar.repeat === 'weekly') base.weeklyInterval = calendar.weeklyInterval ?? 1;
    if (calendar.repeat === 'fortnightly') base.weekSelector = calendar.weekSelector ?? DEFAULT_WEEK_SELECTOR;

    return base;
  }

  base.monthlyOn = calendar.monthlyOn ?? 'dayOfMonth';
  if (calendar.repeat === 'quarterly') base.monthInterval = calendar.monthInterval ?? DEFAULT_MONTH_INTERVAL;

  if (base.monthlyOn === 'dayOfMonth') {
    base.daysOfMonth = sortMonthDays(calendar.daysOfMonth ?? []);
  } else {
    base.nths = [...(calendar.nths ?? [])].sort(compareNths);
    base.weekday = calendar.weekday ?? DEFAULT_WEEKDAY;
  }

  return base;
}

/** Ascending, with 'last' pushed to the end — matches the engine's own ordering in describe(). */
function sortMonthDays(days: readonly (number | 'last')[]): (number | 'last')[] {

  return [...days].sort((a, b) => {

    if (a === 'last') return b === 'last' ? 0 : 1;
    if (b === 'last') return -1;

    return a - b;
  });
}

function compareNths(a: number | 'last', b: number | 'last'): number {

  if (a === 'last') return b === 'last' ? 0 : 1;
  if (b === 'last') return -1;

  return a - b;
}

/**
 * Everything wrong with the draft, in field order, as the shared model validator sees it.
 *
 * The editor asks `validateTaskSchedule` rather than keeping its own rule list, because the API guard
 * asks the same function: a draft that passes here is one the server will accept, and a rule that
 * changes changes in one place. It runs on the PRUNED model (`toModel`), not the raw draft — the draft
 * deliberately carries every branch and every repeat's fields at once so a tab switch loses nothing,
 * and the validator rightly rejects a stale `dailyInterval` sitting on a weekly rule.
 *
 * The `ends.type === 'onDate'` case is the one thing the validator cannot see: an empty date string
 * is a half-finished choice in the editor and never reaches storage, so it is checked here.
 */
export function draftIssues(draft: ScheduleEditorDraft): ScheduleValidationIssue[] {

  if (draft.kind === 'calendar') {
    const ends = draft.calendar.ends;

    if (ends.type === 'onDate' && !ends.date) return [{ field: 'calendar.ends.date', message: 'Pick the date the schedule ends on' }];
  }

  return validateTaskSchedule(toModel(draft));
}

/** Whether Save may be pressed. */
export function isDraftValid(draft: ScheduleEditorDraft): boolean {

  return draftIssues(draft).length === 0;
}
